import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Col, ListGroup, Badge, OverlayTrigger, Tooltip, Spinner, FormControl } from 'react-bootstrap';
import ProductModal from './ProductModal';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { getItemMeasure } from '../services';

const Products = ({ brand, category, clearBrandFilter, clearCategoryFilter, onProductClick }) => {
    const [existingProducts, setExistingProducts] = useState([]);
    const [nextProductsUrl, setNextProductsUrl] = useState(null);
    const [showProductModal, setShowProductModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [loading, setLoading] = useState(false); // Loading state
    const listGroupRef = useRef(null);

    const token = localStorage.getItem('token');

    const pageSize = 10;

    const fetchProducts = useCallback(async (inputValue = '', nextUrl = null, brandId = null, categorySlug = null) => {
        setLoading(true);
        const params = { page_size: pageSize };
        if (inputValue && inputValue.length >= 3) {
            params.search = inputValue;
        }
        if (brandId) {
            params.brand_id = brandId;
        }
        if (categorySlug) {
            params.category_slug = categorySlug;
        }

        const url = nextUrl || `${process.env.REACT_APP_BACKEND_API_URL}/highgarden/grocery-products/`;

        try {
            const response = await axios.get(url, {
                params,
                headers: {
                    'Authorization': `Token ${token}`
                },
            });

            if (nextUrl) {
                setExistingProducts(prevProducts => [...prevProducts, ...response.data.results]);
            } else {
                setExistingProducts(response.data.results);
            }
            setNextProductsUrl(response.data.next);
        } catch (error) {
            console.error('Error fetching products:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        setExistingProducts([]); // Reset products list on brand/category change
        setNextProductsUrl(null); // Reset pagination URL on brand/category change
        fetchProducts('', null, brand ? brand.id : null, category ? category.slug : null);
    }, [brand, category, fetchProducts]);

    useEffect(() => {
        const handleScroll = () => {
            if (listGroupRef.current) {
                const { scrollTop, scrollHeight, clientHeight } = listGroupRef.current;
                if (scrollTop + clientHeight >= scrollHeight - 1 && nextProductsUrl && !loading) {
                    fetchProducts('', nextProductsUrl, brand ? brand.id : null, category ? category.slug : null);
                }
            }
        };

        const listGroupElement = listGroupRef.current;
        if (listGroupElement) {
            listGroupElement.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (listGroupElement) {
                listGroupElement.removeEventListener('scroll', handleScroll);
            }
        };
    }, [nextProductsUrl, brand, category, loading, fetchProducts]);

    const debouncedFetchProducts = useCallback((inputValue) => {
        const debouncedFn = _.debounce(() => {
            setExistingProducts([]); // Clear the products list on new search
            setNextProductsUrl(null); // Reset pagination URL on new search
            fetchProducts(inputValue, null, brand ? brand.id : null, category ? category.slug : null);
        }, 300);
        debouncedFn();
    }, [fetchProducts, brand, category]);

    const handleProductSearchChange = (e) => {
        setProductSearchTerm(e.target.value);
        debouncedFetchProducts(e.target.value);
    };

    const handleShowProductModal = (product = null) => {
        setSelectedProduct(product);
        setShowProductModal(true);
    };

    // Using shared `getItemMeasure` from services.js

    const handleAddFavorite = async (productId) => {
        console.log("Adding product ID:", productId);
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-products/`, {
                product_id: productId
            }, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`
                }
            });
            // Update the local state to reflect the product as favorited
            setExistingProducts((prevProducts) =>
                prevProducts.map((product) =>
                    product.id === productId ? { ...product, is_favorite: true } : product
                )
            );
            toast.success("Product added to favorites successfully!");
        } catch (error) {
            toast.error("Error adding product to favorites!");
            console.error("Error adding favorite product:", error);
        }
    };

    const handleRemoveFavorite = async (productId) => {
        console.log("Removing product ID:", productId);
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_API_URL}/highgarden/user/favorite-products/${productId}/`, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('token')}`
                }
            });
            console.log("Product ID removed:", productId);
            // Update the local state to reflect the product as not favorited
            setExistingProducts((prevProducts) =>
                prevProducts.map((product) =>
                    product.id === productId ? { ...product, is_favorite: false } : product
                )
            );
            fetchProducts(); // Refresh the list of favorite products
            toast.success("Product removed from favorites successfully!");
        } catch (error) {
            toast.error("Error removing favorite products:", error);
            console.error("Error removing favorite products:", error);
        }
    };

    return (
        <Col>
            <h5>
                Products {brand && (
                    <span>
                        (Brand: {brand.name})
                        {brand &&
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-owner">
                                    Clear filter
                                </Tooltip>}>
                                <i className="bi bi-x-circle"
                                    style={{ cursor: 'pointer', marginLeft: '8px', position: 'relative', top: '2px' }}
                                    onClick={clearBrandFilter}>
                                </i>
                            </OverlayTrigger>}
                    </span>
                )}
                {category && (
                    <span>
                        (Category: {category.name})
                        {category &&
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-owner">
                                    Clear filter
                                </Tooltip>}>
                                <i className="bi bi-x-circle"
                                    style={{ cursor: 'pointer', marginLeft: '8px', position: 'relative', top: '2px' }}
                                    onClick={clearCategoryFilter}>
                                </i>
                            </OverlayTrigger>}
                    </span>
                )}
            </h5>
            <FormControl
                type="text"
                placeholder="Search Products"
                value={productSearchTerm}
                onChange={handleProductSearchChange}
                style={{ marginBottom: '10px' }}
            />
            <ListGroup ref={listGroupRef} className="hidden-scrollbar" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {existingProducts.map(product => (
                    <ListGroup.Item
                        key={product.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onProductClick(product)}
                    >
                        <div className="d-flex justify-content-between w-100">
                            <div>
                                {product.is_owner ?
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="tooltip-owner">
                                            Custom Product
                                        </Tooltip>}
                                    >
                                        <i className="bi bi-tropical-storm" style={{ marginRight: '8px' }}></i>
                                    </OverlayTrigger>
                                    : null}
                                {product.name}
                                <Badge className="bg-success" style={{ marginLeft: '8px' }}>{getItemMeasure(product)}</Badge>

                                <OverlayTrigger
                                    overlay={<Tooltip id="tooltip-owner">
                                        {product.description ? product.description : 'No description'}
                                    </Tooltip>}
                                >
                                    <i className="bi bi-info-circle" style={{ marginLeft: '8px' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleShowProductModal(product);
                                        }
                                        }></i>
                                </OverlayTrigger>
                            </div>
                            <div className="d-flex align-items-center">
                                {product.product_brand.id ? (<Badge className='bg-danger me-2'>{product.product_brand.name}</Badge>) : null}
                                {/* Star button to add or remove from favorites */}
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id="tooltip-owner">
                                        {product.is_favorite ? "Remove from favorites" : "Add to favorites"}
                                    </Tooltip>}
                                >
                                    <i
                                        className={`bi ${product.is_favorite ? 'bi-star-fill' : 'bi-star'}`}
                                        style={{
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s, color 0.2s',
                                            color: product.is_favorite ? '#333333' : '#6c757d',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (product.is_favorite) {
                                                handleRemoveFavorite(product.is_favorite_id);
                                            } else {
                                                handleAddFavorite(product.id);
                                            }
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.transform = 'scale(1.2)';
                                            e.target.style.color = '#ffd700'; // Gold color on hover
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.transform = 'scale(1)';
                                            e.target.style.color = product.is_favorite ? '#ffc107' : '#6c757d'; // Default color
                                        }}
                                    ></i>
                                </OverlayTrigger>
                            </div>
                        </div>
                    </ListGroup.Item>
                ))}
            </ListGroup>
            {
                loading && (
                    <div className="d-flex justify-content-center mt-2">
                        <Spinner animation="border" />
                    </div>
                )
            }
            <ProductModal
                show={showProductModal}
                handleClose={() => setShowProductModal(false)}
                refreshProducts={fetchProducts}
                product={selectedProduct}
            />
        </Col >
    );
};

export default Products;
